/*
 * Books, Taxes (owner, 2026-11-01): pick where the business files and how it is set up, then Find tax savings. Claude
 * reads this year's federal and state rules on the web and the year's books, and lists ways to pay less tax, lines to
 * fix and dates to keep (api books/tax.ts). The AI bookkeeper switches live here too: post what it is sure of, and go
 * over new bank lines by itself.
 */
import { useEffect, useState } from "react";
import { ENTITY_KINDS, ENTITY_WORDS, type TaxProfile, type TaxReview } from "@steward/shared";
import { useBookTax, useRunTaxReview } from "../../api/books";
import { useSettings, useUpdateSettings } from "../../api/hooks";
import { useSession } from "../../app/session";
import { Choice, Combobox, FormField, InputText } from "../../components/fields";
import { shortDate } from "../../components/format";
import { useToast } from "../../components/overlays";
import { Button, Card, EmptyState, Skeleton, StatusLabel, Switch } from "../../components/parts";
import { errText } from "./common";

const STATES: [string, string][] = [
  ["AL", "Alabama"],
  ["AK", "Alaska"],
  ["AZ", "Arizona"],
  ["AR", "Arkansas"],
  ["CA", "California"],
  ["CO", "Colorado"],
  ["CT", "Connecticut"],
  ["DE", "Delaware"],
  ["DC", "Washington DC"],
  ["FL", "Florida"],
  ["GA", "Georgia"],
  ["HI", "Hawaii"],
  ["ID", "Idaho"],
  ["IL", "Illinois"],
  ["IN", "Indiana"],
  ["IA", "Iowa"],
  ["KS", "Kansas"],
  ["KY", "Kentucky"],
  ["LA", "Louisiana"],
  ["ME", "Maine"],
  ["MD", "Maryland"],
  ["MA", "Massachusetts"],
  ["MI", "Michigan"],
  ["MN", "Minnesota"],
  ["MS", "Mississippi"],
  ["MO", "Missouri"],
  ["MT", "Montana"],
  ["NE", "Nebraska"],
  ["NV", "Nevada"],
  ["NH", "New Hampshire"],
  ["NJ", "New Jersey"],
  ["NM", "New Mexico"],
  ["NY", "New York"],
  ["NC", "North Carolina"],
  ["ND", "North Dakota"],
  ["OH", "Ohio"],
  ["OK", "Oklahoma"],
  ["OR", "Oregon"],
  ["PA", "Pennsylvania"],
  ["RI", "Rhode Island"],
  ["SC", "South Carolina"],
  ["SD", "South Dakota"],
  ["TN", "Tennessee"],
  ["TX", "Texas"],
  ["UT", "Utah"],
  ["VT", "Vermont"],
  ["VA", "Virginia"],
  ["WA", "Washington"],
  ["WV", "West Virginia"],
  ["WI", "Wisconsin"],
  ["WY", "Wyoming"],
];

export function Taxes({ today }: { today: string }) {
  return (
    <div className="stack">
      <AiHelper />
      <TaxSetup />
      <TaxSavings today={today} />
    </div>
  );
}

/** How much the AI does by itself in Books. */
function AiHelper() {
  const { company } = useSession();
  const s = useSettings(company.id);
  const save = useUpdateSettings();
  const toast = useToast();
  if (!s.data) return null;
  const b = s.data.settings.books;
  const set = (patch: { aiPosts?: boolean; aiAfterSync?: boolean }) =>
    save.mutate({ books: patch }, { onSuccess: () => toast("Saved."), onError: (e) => toast(errText(e), { kind: "error" }) });
  return (
    <Card title="AI bookkeeper">
      <div className="stack">
        <label className="row">
          <Switch checked={b.aiAfterSync} onChange={(v) => set({ aiAfterSync: v })} label="Review new bank lines by itself" />
          <span>
            Review new bank lines by itself
            <span className="muted small" style={{ display: "block" }}>
              Every night after the bank pull, the AI puts a category on each new line. You do not have to press anything.
            </span>
          </span>
        </label>
        <label className="row">
          <Switch checked={b.aiPosts} onChange={(v) => set({ aiPosts: v })} label="Post what it is sure of" />
          <span>
            Post what it is sure of
            <span className="muted small" style={{ display: "block" }}>
              Lines it is sure of go straight to Posted, marked Posted by AI, and clear transfers between your accounts get
              paired. You only check the ones it was not sure of. You can still change any of them.
            </span>
          </span>
        </label>
      </div>
    </Card>
  );
}

/** Where the business files taxes and how it is set up. Saved as the owner picks. */
function TaxSetup() {
  const { company } = useSession();
  const s = useSettings(company.id);
  const save = useUpdateSettings();
  const toast = useToast();
  const [city, setCity] = useState("");
  const [notes, setNotes] = useState("");
  const tax = s.data?.settings.books.tax;
  useEffect(() => {
    if (tax) {
      setCity(tax.city);
      setNotes(tax.notes);
    }
  }, [tax?.city, tax?.notes]);
  if (!tax) return <Skeleton rows={4} />;
  const set = (patch: Partial<TaxProfile>) =>
    save.mutate(
      { books: { tax: patch } },
      { onSuccess: () => toast("Saved."), onError: (e) => toast(errText(e), { kind: "error" }) },
    );
  return (
    <Card title="Where and how you file">
      <p className="muted">The AI uses this for its tax notes and your tax savings list. Your tax pro can check it.</p>
      <div className="form-grid">
        <div className="span-6">
          <Combobox
            label="State"
            value={tax.state}
            onChange={(v) => set({ state: v })}
            options={STATES.map(([v, label]) => ({ value: v, label }))}
          />
        </div>
        <div className="span-6">
          <InputText
            label="City and parish or county"
            placeholder="New Orleans, Orleans Parish"
            value={city}
            maxLength={80}
            onChange={(e) => setCity(e.target.value)}
            onBlur={() => city !== tax.city && set({ city })}
          />
        </div>
        <div className="span-12">
          <Choice
            label="How the business is set up"
            value={tax.entity}
            onChange={(v) => set({ entity: v })}
            options={ENTITY_KINDS.map((k) => ({ value: k, label: ENTITY_WORDS[k] }))}
            description="Not sure? It is on your last tax return, or ask your tax pro."
          />
        </div>
        <div className="span-6">
          <Choice
            label="How you pay yourself"
            value={tax.ownerPay}
            onChange={(v) => set({ ownerPay: v })}
            options={[
              { value: "draw", label: "I take money out", hint: "Owner draws" },
              { value: "salary", label: "A paycheck", hint: "Salary on payroll" },
            ]}
          />
        </div>
        <div className="span-6">
          <Choice
            label="Car and truck costs"
            value={tax.vehicle}
            onChange={(v) => set({ vehicle: v })}
            options={[
              { value: "miles", label: "By the mile", hint: "IRS rate per mile" },
              { value: "actual", label: "Real costs", hint: "Gas, repairs, insurance" },
            ]}
          />
        </div>
        <div className="span-12">
          <label className="row">
            <Switch checked={tax.homeOffice} onChange={(v) => set({ homeOffice: v })} label="Home office" />
            <span>I run the business from a room at home used only for work</span>
          </label>
        </div>
        <div className="span-12">
          <FormField
            label="Anything else the AI should know"
            description="Other income, a spouse who works in the business, a big buy coming up."
          >
            <textarea
              rows={3}
              maxLength={1000}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onBlur={() => notes !== tax.notes && set({ notes })}
            />
          </FormField>
        </div>
      </div>
    </Card>
  );
}

function TaxSavings({ today }: { today: string }) {
  const { company } = useSession();
  const tax = useBookTax(company.id);
  const run = useRunTaxReview();
  const toast = useToast();
  const year = Number(today.slice(0, 4));
  const go = (y: number) => run.mutate({ year: y }, { onError: (e) => toast(errText(e), { kind: "error" }) });
  const review = run.data?.review ?? tax.data?.review ?? null;
  return (
    <Card
      title="Tax savings"
      action={
        <div className="row tight">
          {today.slice(5) < "04-16" ? (
            <Button size="small" type="tertiary" loading={run.isPending} onClick={() => go(year - 1)}>
              For {String(year - 1)}
            </Button>
          ) : null}
          <Button size="small" icon="sparkle" loading={run.isPending} onClick={() => go(year)}>
            Find tax savings
          </Button>
        </div>
      }
    >
      {run.isPending ? (
        <p className="muted">
          The AI is reading this year&apos;s tax rules and your books. This takes 3 to 5 minutes. You can stay on this page.
        </p>
      ) : null}
      {!review ? (
        tax.isLoading ? (
          <Skeleton rows={3} />
        ) : (
          <EmptyState icon="sparkle" title="No tax savings list yet">
            Fill in where and how you file, then press Find tax savings. The AI reads this year&apos;s rules for your state and
            your books, and lists ways to pay less tax.
          </EmptyState>
        )
      ) : (
        <TaxList review={review} />
      )}
      <p className="muted small">
        A guide to talk over with your tax pro, not a filed return. Moves marked Ask your tax pro need their OK first.
      </p>
    </Card>
  );
}

function TaxList({ review }: { review: TaxReview }) {
  return (
    <div className="stack">
      <p className="muted small">
        For {String(review.year)}, made {shortDate(review.at.slice(0, 10))}
      </p>
      {review.summary ? <p>{review.summary}</p> : null}
      {review.moves.length ? (
        <div className="stack tight">
          <h3>Ways to pay less</h3>
          {review.moves.map((m) => (
            <div key={m.title} className="tax-move">
              <div className="row tight">
                <b>{m.title}</b>
                {m.saves ? <StatusLabel status="success">Could save {m.saves}</StatusLabel> : null}
                {m.askPro ? <StatusLabel status="warning">Ask your tax pro</StatusLabel> : null}
              </div>
              <span>{m.why}</span>
              <span className="muted">
                {m.how}
                {m.when ? `. ${m.when}.` : ""}
              </span>
            </div>
          ))}
        </div>
      ) : null}
      {review.fixes.length ? (
        <div className="stack tight">
          <h3>Fix in your books</h3>
          {review.fixes.map((f) => (
            <div key={f.what} className="tax-move">
              <b>{f.what}</b>
              <span className="muted">{f.why}</span>
            </div>
          ))}
        </div>
      ) : null}
      {review.dates.length ? (
        <div className="stack tight">
          <h3>Dates to keep</h3>
          {review.dates.map((d) => (
            <div key={`${d.day}${d.what}`} className="row tight">
              <b style={{ minWidth: 90 }}>{d.day}</b>
              <span>{d.what}</span>
            </div>
          ))}
        </div>
      ) : null}
      {review.sources.length ? (
        <p className="muted small">
          Read from{" "}
          {review.sources.map((s, i) => (
            <span key={s.url}>
              {i ? ", " : ""}
              <a href={s.url} target="_blank" rel="noreferrer">
                {s.title || new URL(s.url).hostname}
              </a>
            </span>
          ))}
        </p>
      ) : null}
    </div>
  );
}
